import { FC, ReactNode } from "react";
import { useParams, Navigate } from "react-router-dom";
import { useCurrentUser } from "../api/users/userQueries";
import { useGetClients } from "../api/clients/clientQueries";
import { Spinner, Flex, Text } from "@chakra-ui/react";

interface ClientAccessGuardProps {
    children: ReactNode;
}

/**
 * Route guard component that ensures the current user has access to the specific client
 */
export const ClientAccessGuard: FC<ClientAccessGuardProps> = ({ children }) => {
    const { clientId } = useParams();
    const { isLoading: isLoadingUser } = useCurrentUser();
    const { data: userClients = [], isLoading: isLoadingClients } = useGetClients();

    // Show loading spinner while checking access
    if (isLoadingUser || isLoadingClients) {
        return (
            <Flex justify="center" align="center" height="100vh">
                <Spinner size="xl" color="blue.500" />
                <Text ml={3}>Verifying access...</Text>
            </Flex>
        );
    }

    // If no client ID in the route, navigate to unauthorized
    if (!clientId) {
        return <Navigate to="/Unauthorized" replace />;
    }

    // Check if user has access to this specific client
    const hasClientAccess = userClients.some(client => client.id === clientId);

    if (!hasClientAccess) {
        return <Navigate to="/Unauthorized" replace />;
    }

    // User has access, render the protected content
    return <>{children}</>;
};
